import CheckIcon from '@mui/icons-material/Check';
import { createColumnHelper, Row } from '@tanstack/react-table';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys } from '../../../api/config';
import { useOrganizationQuery } from '../../../api/queries/use-organization-query';
import { Customer, LocationGroup, OrganizationLocation } from '../../../types';
import { getSingleColorTagStyle } from '../../drafts-loads/utils/drafts-loads-utils';

export const useLocationColumns = () => {
    const { data: customers } = useOrganizationQuery<Customer[]>(OrgQueryKeys.customers);
    const { data: locationGroups = [] } = useOrganizationQuery<LocationGroup[]>(OrgQueryKeys.locationGroups);
    const columnHelper = createColumnHelper<OrganizationLocation>();

    const columns = React.useMemo(() => {
        const c = [
            columnHelper.accessor('name', {
                header: L.name(),
                cell: (info) => info.getValue(),
                size: 150,
                enableColumnFilter: false
            }),
            // search does not work right when a null value is in this list
            // but it MIGHT actually be, if 1st value in the list is null is what is
            // stoping the searcher from working?!?
            columnHelper.accessor((info) => {
                return info.description ? info.description : '';
            }, {
                header: L.description(),
                cell: (info) => info.getValue(),
                size: 150,
                enableColumnFilter: false
            }),
            columnHelper.accessor('customerId', {
                header: L.customer(),
                cell: (info) => {
                    const customer = customers?.find((c) => c.id === info.getValue());
                    return customer?.customerName;
                },
                size: 150,
                getUniqueValues: (location) => customers?.filter(c => c.id === location.customerId).map(c => c.customerName) || [],
                enableColumnFilter: true,
                filterFn: (row: Row<any>, columnId: string, filterValue: string[] | undefined) => {
                    const locationCustomerId = row.getValue(columnId);
                    return (!filterValue || filterValue.length === 0)
                        || filterValue.some(filterString => customers?.find(c => c.id === locationCustomerId)?.customerName === filterString);
                }
            }),
            columnHelper.accessor((location) => {
                // @ts-ignore
                return L[`locationTypeValue${location.organizationLocationTypeId}`]();
            }, {
                header: L.locationType(),
                cell: (info) => {
                    // @ts-ignore
                    return L[`locationTypeValue${info.row.original.organizationLocationTypeId}`]();
                },
                size: 125,
                enableColumnFilter: true,
                filterFn: 'arrIncludesSome',
            }),
            columnHelper.accessor((location) => {
                const display = [location.address1, location.city, location.state].filter(Boolean).join(', ');
                return `${display} ${location.postalCode}`;
            }, {
                header: L.address(),
                cell: (info) => info.getValue(),
                size: 300,
                enableColumnFilter: false
            }),
            columnHelper.accessor('hasScale', {
                header: L.hasWeighScale(),
                cell: (info) => info.getValue() ? L.yes() : L.no(),
                size: 150,
                enableColumnFilter: false
            }),
            columnHelper.accessor('groupId', {
                id: 'location-group',
                header: L.group(),
                cell: (info) => (
                    <div style={ getSingleColorTagStyle(info.getValue(), locationGroups) }>
                        {locationGroups.find(locationGroup => locationGroup.id === info.getValue())?.name}
                    </div>
                ),
                size: 100,
                enableColumnFilter: true,
                meta: locationGroups,
                getUniqueValues: (location) => location.groupId ? [location.groupId] : [], // Ensure we only return the group ID if it exists
                filterFn: (row: Row<any>, columnId: string, filterValue: string[] | undefined) => (!filterValue || filterValue.length === 0)
                    || filterValue.some(filterGroupId => locationGroups.find(lg => lg.id === row.getValue(columnId))?.id === Number(filterGroupId))
            }),
            columnHelper.accessor((location) => {
                return location.enabled ? L.enabled() : L.disabled();
            }, {
                id: 'enabled',
                header: L.enabled(),
                cell: (info) => info.row.original.enabled && <CheckIcon color="primary" />,
                enableColumnFilter: true,
                size: 100,
            })
        ];

        return c;
    }, [customers]);

    return columns
};
